/**
 * Converts a hex color to RGB
 *
 * @param  {string} color - #rrggbb or #rgb
 * @return {string} rgb(r, g, b)
 */
export function convertHexToRGB(color) {
    if (color.length === 4) {
        let extendedColor = '#';
        for (let i = 1; i < color.length; i++) {
            extendedColor += color.charAt(i) + color.charAt(i);
        }
        /* eslint-disable no-param-reassign */
        color = extendedColor;
        /* eslint-enable no-param-reassign */
    }

    const values = {
        r: parseInt(color.substr(1, 2), 16),
        g: parseInt(color.substr(3, 2), 16),
        b: parseInt(color.substr(5, 2), 16),
    };

    return `rgb(${values.r}, ${values.g}, ${values.b})`;
}

/**
 * Converts a RGB color to HEX
 *
 * @param  {string} color - rgb()
 * @return {string} hex value #rrggbb
 */
export function rgbToHex(r, g, b) {
    // convert to base 16, prepend a 0 for single characters, join and return
    const hexValue = [r, g, b].map((x) => `0${x.toString(16)}`.slice(-2)).join('');

    return `#${hexValue}`;
}

/**
 * Returns an object with the type and values of a color
 *
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @return {{type: string, values: number[]}} Color object
 */
export function decomposeColor(color) {
    if (color.charAt(0) === '#') {
        return decomposeColor(convertHexToRGB(color));
    }

    const marker = color.indexOf('(');
    const type = color.substring(0, marker);
    let values = color.substring(marker + 1, color.length - 1).split(',');
    values = values.map((value) => parseFloat(value));

    return { type, values };
}

/**
 *
 * Checks there is input in the color input field
 *
 * @param {string} colorString
 * @return {boolean}
 */
const notEmptyInput = (colorString) => colorString.length > 0;

/**
 *
 * Checks for valid Hex color. Returns true if it is valid
 *
 * @param {string} colorString
 * @return {boolean} returns true if it is valid color
 */
export const validHexColor = (colorString) => colorString.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/gim) !== null;

/**
 *
 * Checks for valid rgb and rgba first 3 numbers are between 0-255
 *
 * @param {array} colorIntArray
 * @return {boolean} return true if number is between 0-255
 */
export const rgbInt = (colorIntArray) => {
    if (colorIntArray.length !== 3) {
        return false;
    }
    // return true if number is between 0-255
    return colorIntArray.every((colorInt) => colorInt >= 0 && colorInt <= 255);
};

/**
 *
 * Check for rgba float is between 0-1
 *
 * @param {array} colorIntArray
 * @return {boolean}
 */
export const validRgbaColor = (colorIntArray) => {
    // first checks if it is rgba color
    if (colorIntArray.length !== 4) {
        return false;
    }
    // if rgba color, first check the float at the end
    const floatingInteger = colorIntArray.pop();
    // check if float is between 0 and 1
    if (!(floatingInteger >= 0 && floatingInteger <= 1)) {
        return false;
    }
    // finally, check if 3 numbers are between 0-255
    return rgbInt(colorIntArray);
};

/**
 * Color validator method. Checks for valid color input by calling rgb/rgba/hex validator function
 *
 * @param {string} colorString String input includes rgb/rgba/hex syntax i.e: rgb(100,100,100)
 * @return {boolean}
 */
export const checkIfValidColor = (colorString) => {
    // check there is color in the input field
    if (notEmptyInput(colorString)) {
        // check for valid hex color
        if (validHexColor(colorString)) {
            return true;
        }
        // check for rgb or rgba
        // array initialized to hold digits between parens in rgb or rgba color
        let colorIntArray = [];
        const inputColor = colorString.match(/\(([^)]+)\)/);
        // if there are two parens in colorString, then grab the digits and save to colorIntArray
        if (inputColor) {
            colorIntArray = inputColor[1].split(',').map(parseFloat);
        }
        // validRgbaColor also calls rgbInt to check first three integers
        if (/rgba/.test(colorString)) {
            return validRgbaColor(colorIntArray);
        }
        // if rgb color, only check if first three integers are valid
        if (/rgb/.test(colorString)) {
            return rgbInt(colorIntArray);
        }
    }
    return false;
};

/**
 * Contrast validator method. Checks for valid accessibility standards by converting colors
 * to RGB and comparing luminance values as a ratio.
 *
 * @param {string} backgroundColor rgb/rgba/hex syntax i.e: rgb(100,100,100)
 * @param {string} foregroundColor rgb/rgba/hex syntax i.e: rgb(100,100,100)
 * @param {string} tier Which accessibility standard to test - 'AA' || 'AAA'- default AAA
 * @param {string} size Which font size accessibility standard to test - 'sm' || 'lg' - default sm
 * @return {{passesAccessibilityTest: boolean, toOneRatio: string}}
 */
export const checkIfAccessible = (backgroundColor, foregroundColor, tier = 'AAA', size = 'sm') => {
    if (!checkIfValidColor(backgroundColor) || !checkIfValidColor(foregroundColor)) {
        return false;
    }

    // function from https://stackoverflow.com/a/9733420/3695983
    function luminance(r, g, b) {
        const a = [r, g, b].map((v) => {
            const value = (v * 1) / 255;
            return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
        });
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    }

    function calculateRatio() {
        // read the colors and transform them into rgb format
        // we are making some pretty big assumptions that we will only be processing RGB and HEX
        const color1 = backgroundColor;
        const color2 = foregroundColor;
        const color1rgb = decomposeColor(color1).values;
        const color2rgb = decomposeColor(color2).values;

        // calculate the relative luminance
        const color1luminance = luminance(color1rgb[0], color1rgb[1], color1rgb[2]);
        const color2luminance = luminance(color2rgb[0], color2rgb[1], color2rgb[2]);

        // calculate the color contrast ratio
        const ratio =
            color1luminance > color2luminance
                ? (color2luminance + 0.05) / (color1luminance + 0.05)
                : (color1luminance + 0.05) / (color2luminance + 0.05);

        return ratio;
    }

    const ratio = calculateRatio();
    // show results depending on WCAG requirements

    const passesAccessibilityTest =
        tier === 'AA'
            ? // testing for AA compliance
              size === 'sm'
                ? ratio < 1 / 4.5 // AA for "small" text
                : ratio < 1 / 3 // AA for "large" text
            : // testing for AAA compliance
            size === 'sm'
            ? ratio < 1 / 7 // AAA for "small" text
            : ratio < 1 / 4.5; // AAA for "large" text

    // invert the ratio to display the {Number} to 1 (#:1) ratio measurement
    const toOneRatio = (1 / ratio).toFixed(2);

    return { passesAccessibilityTest, toOneRatio };
};
